// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal, Money } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import {
  getItemReceiptTotalWithAdornments,
  formatOtherCostAmount,
  getOtherCostsTotal,
} from "services/sosInventoryService/purchasingTransaction/totals";
import {
  extractName,
  formatDocumentInfo,
  formatNumberLinkField,
  formatMoneyWithAdornments,
  formatDateTimeToDate,
  truncate,
  extractId,
  extractDescription,
  formatBooleanToYesOrBlank,
  formatQuantityWithUom,
  formatLinkableItem,
  formatMoneyUnroundedWithAdornments,
  getMoneyTotalWithAdornments,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { TYPES, starColumn } from "services/utility/schema";

import { NO_REF_NUMBER_STRING, DEFAULT_DECIMALS_UNROUNDED } from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDateTimeToDate,
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "vendor",
    heading: i18n("columns.Vendor"),
    formatFunc: extractName,
    minWidth: "10em",
    maxWidth: "20em",
    sortable: true,
    default: true,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    minWidth: "15em",
    formatFunc: formatDocumentInfo,
    default: true,
  },
  {
    name: "id",
    heading: `${i18n("columns.System")}`,
    sortDirection: "asc",
    minWidth: "7em",
    align: "right",
    sortable: true,
  },
  {
    name: "total",
    heading: i18n("columns.Amount"),
    formatFunc: formatMoneyWithAdornments,
    align: "right",
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },

  {
    name: "terms",
    heading: i18n("columns.Terms"),
    formatFunc: extractName,
  },
  {
    name: "vendorNotes",
    heading: i18n("columns.VendorNotes"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
  },
  {
    name: "payment",
    heading: i18n("columns.Payment"),
    formatFunc: extractId,
  },
  {
    name: "deposit",
    heading: i18n("columns.Deposit"),
    fieldName: "depositAmount",
    formatFunc: formatMoneyWithAdornments,
    align: "right",
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord(settings) {
  return {
    account: null,
    archived: false,
    autoSerialLotNumbering: settings.autoSerialLotNumbers,
    billDate: null,
    comment: null,
    currency: settings.homeCurrencyRef,
    customFields: [],
    date: new Date(),
    department: null,
    depositAmount: new Money(0),
    depositPercent: null,
    documents: null,
    exchangeRate: new Decimal(1.0),
    hasSignature: false,
    keys: null,
    lines: [],
    linkedTransaction: null,
    location: null,
    number: "",
    otherCosts: [],
    // TODO: Remove `|| "None"` once https://app.clickup.com/t/868g2mqhy is complete
    payment: { id: settings.defaultSyncItemReceipts || "None" }, 
    starred: 0,
    subTotal: new Money(0),
    summaryOnly: false,
    syncToken: 0,
    taxAmount: new Money(0),
    terms: null,
    total: new Money(0),
    updateDefaultCosts: settings.updateDefaultCostsOnPurchase,
    values: null,
    vendor: null,
    vendorInvoiceDueDate: null,
    vendorInvoiceDate: null,
    calculateDueDateFromInvoiceDate: false,
    vendorMessage: "",
    vendorNotes: "",
  };
}

// can omit initialValue if null
// editStr should return the value that should be used on an input element of a
// form; for example, null is a good value for the database, but should be "" on
// an input item
export const LINE_ITEM_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  vendorPartNumber: { type: TYPES.text },
  class: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  customer: { type: TYPES.reference },
  taxCode: { type: TYPES.reference },
  description: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: { type: TYPES.text },
  unitprice: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  amount: {
    type: TYPES.money,
    currency: true,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  received: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  uom: { type: TYPES.reference },
  serials: { type: TYPES.arrayOfReferences, initialValue: [] },
  lot: { type: TYPES.reference },
  lotExpiration: { type: TYPES.date },
  linkedTransaction: { type: TYPES.linkedTransaction, transactionType: "PO" },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  relatedRecords: { initialValue: {} },
  itemDetails: { initialValue: {} },
};

// can omit initialValue if null
// editStr should return the value that should be used on an input element of a
// form; for example, null is a good value for the database, but should be "" on
// an input item
export const OTHER_COST_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null },
  },
  vendor: { type: TYPES.reference },
  class: { type: TYPES.reference },
  amount: {
    type: TYPES.money,
    editStr: formatLineDecimal,
    initialValue: new Money(0),
  },
  bill: { type: TYPES.boolean, initialValue: false },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: ["exchangeRate"],
  MONEY_FIELDS: ["depositAmount", "subTotal", "taxAmount", "total"],
  DATE_FIELDS: ["vendorInvoiceDate", "vendorInvoiceDueDate"],
  DATETIME_FIELDS: ["date"],
  NON_UTC_DATETIME_FIELDS: [],
};

export const REFERENCE_FIELDS = ["location", "terms", "vendor", "currency"];
export const OTHER_COST_REFERENCE_FIELDS = ["vendor", "class"];

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "transfer",
  "archive",
  "delete",
  "returnToVendor",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [["pdf", "email"], "delete", "archive", "sync"];

export const ITEM_CALCULATED_FIELDS = ["purchaseCost"];

export const QUICK_VIEW_CONFIG = {
  signatureLinesLabel: i18n("colon.ReceivedBy"),
  tableConfig: [{ type: "lines" }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.ReceiptNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [
    { label: i18n("frmLabel.Vendor"), name: "vendor", formatFunc: extractName },
    {
      label: i18n("frmLabel.Location"),
      name: "location",
      formatFunc: extractName,
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
    {
      name: "unitprice",
      formatFunc: formatMoneyUnroundedWithAdornments,
      heading: i18n("columns.Rate"),
      align: "right",
    },
    {
      name: "amount",
      formatFunc: formatMoneyWithAdornments,
      footerFunc: getMoneyTotalWithAdornments,
      heading: i18n("columns.Amount"),
      isTotalColumn: true,
      align: "right",
    },
  ],
  otherCosts: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      heading: i18n("columns.Description"),
      formatFunc: () => "other cost",
      align: "left",
      colSpan: 3,
    },

    {
      name: "amount",
      formatFunc: formatOtherCostAmount,
      footerFunc: getOtherCostsTotal,
      combinedFooterFunc: getItemReceiptTotalWithAdornments,
      heading: i18n("columns.Amount"),
      align: "right",
    },
  ],
};
